// src/ui/components/hooks/useFollowCamera.ts
import { useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { CameraControls } from '@react-three/drei';
import CameraControlsImpl from 'camera-controls';
import { Group } from 'three';
import { useGameStore } from '../../../core/store/gameStore';
import { CAMERA_POSITION, CAMERA_LOOKAT } from '../CameraViewControl';

interface UseFollowCameraProps {
  characterRef: React.MutableRefObject<Group | null> | null;
  controlsRef: React.MutableRefObject<CameraControls | null>; 
  enabled: boolean;
}

export function useFollowCamera({
  characterRef,
  controlsRef,
  enabled,
}: UseFollowCameraProps) {
  const { gl } = useThree();
  const isMobile = useGameStore((state) => state.isMobile);

  useEffect(() => {
    if (!enabled) return;
    
    const onMove = (e: MouseEvent) => {
      controlsRef.current?.rotate(-e.movementX * 0.002, -e.movementY * 0.002, true);
    };

    document.addEventListener('mousemove', onMove);
    return () => document.removeEventListener('mousemove', onMove);
  }, [enabled, controlsRef]);

  // Mobile: take over one-finger rotate so look direction is predictable
  // (left half = joystick, rest of screen = drag to orbit camera)
  useEffect(() => {
    if (!enabled || !isMobile || !controlsRef.current) return;
    controlsRef.current.touches.one = CameraControlsImpl.ACTION.NONE;
  }, [enabled, isMobile, controlsRef]);

  useEffect(() => {
    if (!enabled || !isMobile) return;

    const isValidTouchArea = (t: Touch) => {
      const isJoystickArea = t.clientX < window.innerWidth * 0.45 && t.clientY > window.innerHeight * 0.55;
      const isSideBarArea = t.clientX > window.innerWidth * 0.85 && t.clientY < window.innerHeight * 0.2;
      return !isJoystickArea && !isSideBarArea;
    };

    let lastTouch: { x: number; y: number } | null = null;

    const onTouchStart = (e: TouchEvent) => {
      for (let i = 0; i < e.touches.length; i++) {
        const t = e.touches[i];
        if (isValidTouchArea(t)) {
          lastTouch = { x: t.clientX, y: t.clientY };
          break;
        }
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.cancelable) e.preventDefault();

      let active: Touch | null = null;
      for (let i = 0; i < e.touches.length; i++) {
        const t = e.touches[i];
        if (isValidTouchArea(t)) {
          active = t;
          break;
        }
      }

      if (active && lastTouch) {
        const dx = active.clientX - lastTouch.x;
        const dy = active.clientY - lastTouch.y;
        controlsRef.current?.rotate(-dx * 0.004, -dy * 0.004, true);
        lastTouch = { x: active.clientX, y: active.clientY };
      }
    };

    const onTouchEnd = () => {
      lastTouch = null;
    };

    window.addEventListener('touchstart', onTouchStart, { passive: false });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', onTouchEnd);

    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [enabled, isMobile, controlsRef]);

  // Handle Pointer Lock
  useEffect(() => {
    if (!enabled) return;
    const canvas = gl.domElement;

    const requestLock = async () => {
      // Only request if not already locked to avoid errors
      if (document.pointerLockElement !== canvas) {
        try {
          await canvas.requestPointerLock();
        } catch (e) {
          console.warn('Pointer lock denied:', e);
        }
      }
    };

    // Auto-lock on click
    const handleClick = () => requestLock();
    canvas.addEventListener('click', handleClick);

    // Initial lock attempt
    requestLock();

    return () => {
      canvas.removeEventListener('click', handleClick);
      // Only exit if we are the one holding the lock
      if (document.pointerLockElement === canvas) {
        document.exitPointerLock();
      }
    };
  }, [enabled, gl.domElement]);

  useFrame(() => {
    if (!enabled || !controlsRef.current || !characterRef?.current) return;
    
    const { x, y, z } = characterRef.current.position;
    controlsRef.current.moveTo(x, y + 1.0, z, true);
  });
}